import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { SITE } from "../config/site";
import { useLanguage } from "../i18n/LanguageContext";

const LINKS = [
  { to: "/", key: "nav.home", testid: "nav-home-link" },
  { to: "/sobre", key: "nav.about", testid: "nav-about-link" },
  { to: "/blog", key: "nav.blog", testid: "nav-blog-link" },
  { to: "/agendamento", key: "nav.booking", testid: "nav-booking-link" },
];

const LangToggle = () => {
  const { lang, setLang } = useLanguage();
  return (
    <div className="flex items-center font-mono text-[10px] tracking-[0.2em]" data-testid="lang-toggle">
      {["pt", "en"].map((l, i) => (
        <span key={l} className="flex items-center">
          {i > 0 && <span className="text-[#C5C9BC] mx-1.5">/</span>}
          <button
            data-testid={`lang-toggle-${l}`}
            onClick={() => setLang(l)}
            className={`uppercase p-2 -m-2 transition-colors duration-300 ${
              lang === l ? "text-forest border-b border-forest" : "text-[#9AA096] hover:text-ebony"
            }`}
          >
            {l}
          </button>
        </span>
      ))}
    </div>
  );
};

export const HeaderNav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 border-b transition-[background-color,border-color] duration-500 ${
          scrolled || open
            ? "bg-alabaster/90 backdrop-blur-md border-[#E2E4DC]"
            : "bg-transparent border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex flex-col leading-none" data-testid="brand-link">
            <span className="font-serif text-lg sm:text-xl font-semibold tracking-tight text-ebony">
              {SITE.shortName}
            </span>
            <span className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.3em] text-olive mt-1.5">
              {t("brand.tagline")}
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `relative font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 pb-1 ${
                    isActive ? "text-forest" : "text-[#6C7067] hover:text-ebony"
                  } after:absolute after:left-0 after:bottom-0 after:h-px after:bg-forest after:transition-[width] after:duration-300 ${
                    isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
                  }`
                }
              >
                {t(l.key)}
              </NavLink>
            ))}
            <LangToggle />
            <Link to="/agendamento" data-testid="nav-cta-booking" className="btn-primary !py-3 !px-6">
              {t("nav.cta")}
            </Link>
          </nav>

          <div className="lg:hidden flex items-center gap-5">
            <LangToggle />
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="p-2 text-ebony"
              aria-label="Abrir menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-alabaster pt-28 px-6 lg:hidden"
            data-testid="mobile-menu-panel"
          >
            <nav className="flex flex-col gap-2">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-${l.testid}`}
                    className="block font-serif text-4xl font-medium text-ebony py-3 border-b border-[#E2E4DC]"
                  >
                    {t(l.key)}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.5 }}
                className="pt-8"
              >
                <Link to="/agendamento" data-testid="mobile-nav-cta-booking" className="btn-primary w-full">
                  {t("nav.cta")}
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
